import "../css/header.scss";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FiChevronDown,
  FiGlobe,
  FiHeart,
  FiSearch,
  FiShoppingBag,
  FiUser,
  FiX,
} from "react-icons/fi";
import { CartContext } from "./CartContext";
import siteConfig from "../config/siteConfig";

const SHOP_MENUS = {
  women: {
    label: "Women",
    gender: "Women",
    categories: [
      "Pants",
      "Denim",
      "Knitwear",
      "Blouses",
      "T-Shirts & Tops",
      "Sweatshirts",
    ],
  },
  men: {
    label: "Men",
    gender: "Men",
    categories: [
      "Pants",
      "Denim",
      "Knitwear",
      "T-Shirts",
      "Shirts",
      "Jackets & Coats",
    ],
  },
};

const MORE_LINKS = [
  { label: "FAQs", to: "/faqs" },
  { label: "Privacy policy", to: "/privacy" },
  { label: "Cookie policy", to: "/cookies" },
  { label: "Terms", to: "/terms" },
  { label: "Shipping", to: "/shipping" },
  { label: "Returns", to: "/returns" },
  { label: "Accessibility", to: "/accessibility" },
  { label: "Contact", to: "/contact" },
];

const buildShopUrl = ({ gender, category, tag, sort } = {}) => {
  const params = new URLSearchParams();

  if (gender) params.set("gender", gender);
  if (category) params.set("category", category);
  if (tag) params.set("q", tag);
  if (sort === "newest") params.set("q", "New");

  const query = params.toString();
  return query ? `/shop?${query}` : "/shop";
};

function Header() {
  const { cartCount = 0, wishlist = [] } = useContext(CartContext);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openMega, setOpenMega] = useState(null);
  const [openUtility, setOpenUtility] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState(null);
  const headerRef = useRef(null);
  const searchInputRef = useRef(null);
  const mobileDrawerRef = useRef(null);
  const mobileToggleRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const wishlistCount = wishlist.length;
  const countryCode = siteConfig.locale?.split("-")[1]?.toUpperCase() || "DE";

  const closeDesktopPanels = useCallback(() => {
    setOpenMega(null);
    setOpenUtility(null);
    setSearchOpen(false);
  }, []);

  const closeAllPanels = useCallback(() => {
    closeDesktopPanels();
    setMobileOpen(false);
    setMobileSection(null);
  }, [closeDesktopPanels]);

  const scrollToTop = useCallback(() => {
    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    window.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" });
  }, []);

  useEffect(() => {
    closeAllPanels();
  }, [location.pathname, location.search, closeAllPanels]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        closeDesktopPanels();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeAllPanels();
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeAllPanels, closeDesktopPanels]);

  useEffect(() => {
    if (!searchOpen) return undefined;

    const frame = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [searchOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1120) {
        setMobileOpen(false);
        setMobileSection(null);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!mobileOpen) return undefined;

    const previousActiveElement = document.activeElement;
    const mobileToggle = mobileToggleRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const frame = window.requestAnimationFrame(() => {
      mobileDrawerRef.current?.querySelector("button, a, input")?.focus();
    });

    const trapFocus = (event) => {
      if (event.key !== "Tab" || !mobileDrawerRef.current) return;

      const focusable = Array.from(
        mobileDrawerRef.current.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !element.hasAttribute("hidden"));

      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", trapFocus);

    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", trapFocus);
      document.body.style.overflow = previousOverflow;

      if (previousActiveElement === mobileToggle) {
        mobileToggle?.focus();
      }
    };
  }, [mobileOpen]);

  const toggleMega = (menu) => {
    setOpenUtility(null);
    setSearchOpen(false);
    setOpenMega((current) => (current === menu ? null : menu));
  };

  const handleMegaKeyDown = (event, menu) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpenMega(menu);

      const navItem = event.currentTarget.closest(".site-header__nav-item");
      window.requestAnimationFrame(() => {
        navItem?.querySelector(".site-header__mega a")?.focus();
      });
    }
  };

  const handleSearchSubmit = (event, value = searchValue) => {
    event.preventDefault();
    const query = value.trim();
    navigate(query ? `/shop?q=${encodeURIComponent(query)}` : "/shop");
    setSearchOpen(false);
    setMobileOpen(false);
    setSearchValue("");
    scrollToTop();
  };

  const handleNavClick = () => {
    closeAllPanels();
    scrollToTop();
  };

  const renderMegaMenu = (menuKey) => {
    if (menuKey === "more") {
      if (openMega !== "more") return null;

      return (
        <div
          className="site-header__mega site-header__mega--more"
          id="more-mega-menu"
          role="region"
          aria-label="More information"
        >
          <div className="site-header__more-links">
            <p className="site-header__eyebrow">Information & policies</p>
            <div>
              {MORE_LINKS.map((link) => (
                <Link key={link.to} to={link.to} onClick={handleNavClick}>
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      );
    }

    const menu = SHOP_MENUS[menuKey];
    if (!menu || openMega !== menuKey) return null;

    return (
      <div
        className="site-header__mega"
        id={`${menuKey}-mega-menu`}
        role="region"
        aria-label={`${menu.label} collection`}
      >
        <div className="site-header__mega-inner">
          <div className="site-header__mega-column">
            <p className="site-header__eyebrow">Discover</p>
            <Link
              to={buildShopUrl({ gender: menu.gender, sort: "newest" })}
              onClick={handleNavClick}
            >
              New arrivals
            </Link>
            <Link
              to={buildShopUrl({ gender: menu.gender })}
              onClick={handleNavClick}
            >
              Shop all {menu.label.toLowerCase()}
            </Link>
            <Link
              to={buildShopUrl({ gender: menu.gender, sort: "featured" })}
              onClick={handleNavClick}
            >
              Featured pieces
            </Link>
            <Link
              to={buildShopUrl({ gender: menu.gender, tag: "Sale" })}
              onClick={handleNavClick}
            >
              Special offer
            </Link>
          </div>

          <div className="site-header__mega-column site-header__mega-column--wide">
            <p className="site-header__eyebrow">Collection</p>
            <div className="site-header__mega-links">
              {menu.categories.map((category) => (
                <Link
                  key={category}
                  to={buildShopUrl({ gender: menu.gender, category })}
                  onClick={handleNavClick}
                >
                  {category}
                </Link>
              ))}
            </div>
          </div>

          <div className="site-header__mega-feature">
            <img src={siteConfig.mark} alt="" aria-hidden="true" />
            <div>
              <p className="site-header__eyebrow">Considered essentials</p>
              <p>{siteConfig.description}</p>
              <Link
                to={buildShopUrl({ gender: menu.gender })}
                onClick={handleNavClick}
              >
                Explore {menu.label.toLowerCase()}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderUtilityPanel = () => {
    if (!openUtility) return null;

    const panels = {
      wishlist: {
        title: "Wishlist",
        body: (
          <>
            <p>
              {wishlistCount
                ? `${wishlistCount} saved ${
                    wishlistCount === 1 ? "piece" : "pieces"
                  } in your edit.`
                : "Your edit is ready for pieces you love."}
            </p>
            <Link to="/shop" onClick={handleNavClick}>
              {wishlistCount ? "Continue browsing" : "Discover the collection"}
            </Link>
          </>
        ),
      },
    };

    const panel = panels[openUtility];
    if (!panel) return null;

    return (
      <aside
        className="site-header__utility-popover"
        id={`${openUtility}-utility-panel`}
        aria-label={panel.title}
      >
        <div>
          <p className="site-header__eyebrow">{panel.title}</p>
          <div className="site-header__utility-content">{panel.body}</div>
        </div>
        <button
          type="button"
          className="site-header__popover-close"
          onClick={() => setOpenUtility(null)}
          aria-label={`Close ${panel.title.toLowerCase()}`}
        >
          <FiX aria-hidden="true" />
        </button>
      </aside>
    );
  };

  return (
    <header
      className={`site-header${isScrolled ? " site-header--scrolled" : ""}`}
      ref={headerRef}
    >
      <div className="site-header__row">
        <nav className="site-header__desktop-nav" aria-label="Main navigation">
          <ul>
            <li>
              <Link
                to={buildShopUrl({ sort: "newest" })}
                onClick={handleNavClick}
              >
                New in
              </Link>
            </li>

            {Object.entries(SHOP_MENUS).map(([key, menu]) => (
              <li
                className="site-header__nav-item"
                key={key}
                onMouseEnter={() => {
                  setOpenUtility(null);
                  setSearchOpen(false);
                  setOpenMega(key);
                }}
                onMouseLeave={() => setOpenMega(null)}
                onBlur={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) {
                    setOpenMega(null);
                  }
                }}
              >
                <button
                  type="button"
                  aria-expanded={openMega === key}
                  aria-controls={`${key}-mega-menu`}
                  onClick={() => toggleMega(key)}
                  onKeyDown={(event) => handleMegaKeyDown(event, key)}
                >
                  {menu.label}
                  <FiChevronDown aria-hidden="true" />
                </button>
                {renderMegaMenu(key)}
              </li>
            ))}

            <li>
              <Link to="/about" onClick={handleNavClick}>
                Our story
              </Link>
            </li>
            <li
              className="site-header__nav-item"
              onMouseEnter={() => {
                setOpenUtility(null);
                setSearchOpen(false);
                setOpenMega("more");
              }}
              onMouseLeave={() => setOpenMega(null)}
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) {
                  setOpenMega(null);
                }
              }}
            >
              <button
                type="button"
                aria-expanded={openMega === "more"}
                aria-controls="more-mega-menu"
                onClick={() => toggleMega("more")}
                onKeyDown={(event) => handleMegaKeyDown(event, "more")}
              >
                More
                <FiChevronDown aria-hidden="true" />
              </button>
              {renderMegaMenu("more")}
            </li>
          </ul>
        </nav>

        <Link
          className="site-header__logo"
          to="/"
          aria-label={`${siteConfig.brandName} home`}
          onClick={handleNavClick}
        >
          <img src={siteConfig.logo} alt={siteConfig.brandName} />
        </Link>

        <div
          className="site-header__desktop-tools"
          role="group"
          aria-label="Store tools"
        >
          <Link
            to="/cartpage"
            onClick={handleNavClick}
            aria-label={`Shopping bag, ${cartCount} items`}
          >
            <FiShoppingBag aria-hidden="true" />
            <span className="site-header__count" aria-live="polite">
              {cartCount}
            </span>
          </Link>
        </div>

        <Link
          className="site-header__mobile-bag"
          to="/cartpage"
          onClick={handleNavClick}
          aria-label={`Shopping bag, ${cartCount} items`}
        >
          <FiShoppingBag aria-hidden="true" />
          <span className="site-header__count" aria-live="polite">
            {cartCount}
          </span>
        </Link>
      </div>

      {renderUtilityPanel()}

      {mobileOpen && (
        <>
          <button
            className="site-header__mobile-overlay"
            type="button"
            aria-label="Close navigation"
            onClick={() => setMobileOpen(false)}
          />
          <aside
            ref={mobileDrawerRef}
            className="site-header__mobile-drawer"
            id="mobile-navigation"
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation"
          >
            <div className="site-header__mobile-head">
              <img src={siteConfig.logo} alt={siteConfig.brandName} />
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                aria-label="Close navigation"
              >
                <FiX aria-hidden="true" />
              </button>
            </div>

            <form
              className="site-header__mobile-search"
              role="search"
              onSubmit={handleSearchSubmit}
            >
              <label
                className="site-header__sr-only"
                htmlFor="mobile-header-search"
              >
                Search the collection
              </label>
              <input
                id="mobile-header-search"
                type="search"
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                placeholder="Search the collection"
              />
              <button type="submit" aria-label="Submit search">
                <FiSearch aria-hidden="true" />
              </button>
            </form>

            <nav className="site-header__mobile-nav" aria-label="Mobile shop">
              <Link
                className="site-header__mobile-primary-link"
                to={buildShopUrl({ sort: "newest" })}
                onClick={handleNavClick}
              >
                New in
              </Link>

              {Object.entries(SHOP_MENUS).map(([key, menu]) => (
                <div className="site-header__mobile-section" key={key}>
                  <button
                    type="button"
                    aria-expanded={mobileSection === key}
                    aria-controls={`mobile-${key}-links`}
                    onClick={() =>
                      setMobileSection((current) =>
                        current === key ? null : key
                      )
                    }
                  >
                    {menu.label}
                    <FiChevronDown aria-hidden="true" />
                  </button>
                  {mobileSection === key && (
                    <div
                      className="site-header__mobile-links"
                      id={`mobile-${key}-links`}
                    >
                      <Link
                        to={buildShopUrl({ gender: menu.gender })}
                        onClick={handleNavClick}
                      >
                        Shop all
                      </Link>
                      {menu.categories.map((category) => (
                        <Link
                          key={category}
                          to={buildShopUrl({
                            gender: menu.gender,
                            category,
                          })}
                          onClick={handleNavClick}
                        >
                          {category}
                        </Link>
                      ))}
                      <Link
                        to={buildShopUrl({
                          gender: menu.gender,
                          tag: "Sale",
                        })}
                        onClick={handleNavClick}
                      >
                        Special offer
                      </Link>
                    </div>
                  )}
                </div>
              ))}

              <Link
                className="site-header__mobile-primary-link"
                to="/about"
                onClick={handleNavClick}
              >
                Our story
              </Link>
              <div className="site-header__mobile-section">
                <button
                  type="button"
                  aria-expanded={mobileSection === "more"}
                  aria-controls="mobile-more-links"
                  onClick={() =>
                    setMobileSection((current) =>
                      current === "more" ? null : "more"
                    )
                  }
                >
                  More
                  <FiChevronDown aria-hidden="true" />
                </button>
                {mobileSection === "more" && (
                  <div
                    className="site-header__mobile-links"
                    id="mobile-more-links"
                  >
                    {MORE_LINKS.map((link) => (
                      <Link key={link.to} to={link.to} onClick={handleNavClick}>
                        {link.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </nav>

            <div className="site-header__mobile-utilities">
              <div>
                <FiGlobe aria-hidden="true" />
                <span>
                  {countryCode} / {siteConfig.currency}
                </span>
              </div>
              <Link to="/contact" onClick={handleNavClick}>
                <FiUser aria-hidden="true" />
                <span>Customer care</span>
              </Link>
              <Link to="/shop" onClick={handleNavClick}>
                <FiHeart aria-hidden="true" />
                <span>Wishlist</span>
                <b>{wishlistCount}</b>
              </Link>
              <Link to="/cartpage" onClick={handleNavClick}>
                <FiShoppingBag aria-hidden="true" />
                <span>Shopping bag</span>
                <b>{cartCount}</b>
              </Link>
            </div>
          </aside>
        </>
      )}
    </header>
  );
}

export default Header;
